import * as React from "react";
import {
  HTMLTable,
  Button,
  Menu,
  MenuItem,
  Popover,
  Spinner,
  NonIdealState,
  InputGroup,
  Icon,
  Switch,
  Tag,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthState } from "../../../../context/auth";
import { isFybrAdminUser, isOrgAdminUser } from "../../adminRoles";
import { ConfirmationDialog } from "../../common/ConfirmationDialog";
import {
  STRIPED_ROW_BACKGROUND,
  useAdminTableVirtualizer,
  VirtualizedTableBody,
} from "../../common/VirtualizedTableBody";
import {
  useFolderPermissions,
  useFolderMutations,
  useUpdateFolderOrderTypesMutation,
  type Folder,
} from "../../../../hooks";
import ApiAdmin from "../../../../api/admin";
import { EditFolderDialog } from "./EditFolderDialog";
import { AddFolderDialog } from "./AddFolderDialog";
import { FolderPermissionsDialog } from "./FolderPermissionsDialog";
import { FolderOrderTypesDialog } from "./FolderOrderTypesDialog";
import { OrderTypeMultiSelect } from "./OrderTypeMultiSelect";

const FOLDER_TABLE_COLUMN_COUNT = 4;

function FolderRow(props: {
  folder: Folder;
  index: number;
  orderTypes: any[];
  isFybrAdmin: boolean;
  isActivating: boolean;
  onOpenPermissions: (folder: Folder) => void;
  onOpenOrderTypes: (folder: Folder) => void;
  onEditFolder: (folderId: number) => void;
  onDeleteFolder: (folderId: number) => void;
  onActivateFolder: (folderId: number) => void;
  onOrderTypesChange: (
    folderId: number,
    organizationOrderTypeIds: number[]
  ) => void;
  measureRef: (node: HTMLTableRowElement | null) => void;
}) {
  const {
    folder,
    index,
    orderTypes,
    isFybrAdmin,
    isActivating,
    onOpenPermissions,
    onOpenOrderTypes,
    onEditFolder,
    onDeleteFolder,
    onActivateFolder,
    onOrderTypesChange,
    measureRef,
  } = props;

  const isDeactivated =
    folder.deleted_at !== null && folder.deleted_at !== undefined;
  const readOnlyOrderTypes = !isFybrAdmin || isDeactivated;

  return (
    <tr
      ref={measureRef}
      data-index={index}
      onClick={() => {
        if (!isDeactivated) {
          onOpenPermissions(folder);
        }
      }}
      css={[
        css`
          cursor: ${isDeactivated ? "default" : "pointer"};
        `,
        index % 2 === 0
          ? css`
              td {
                background: ${STRIPED_ROW_BACKGROUND};
              }
            `
          : undefined,
      ]}
    >
      <td>
        <div
          css={css`
            height: 30px;
            display: flex;
            align-items: center;
            color: ${isDeactivated ? "var(--neutral-3)" : "inherit"};
          `}
        >
          {folder.label}
        </div>
      </td>
      <td>
        <div
          css={css`
            height: 30px;
            display: flex;
            align-items: center;
          `}
        >
          <Tag intent={isDeactivated ? "warning" : "success"} minimal>
            {isDeactivated ? "Deactivated" : "Active"}
          </Tag>
        </div>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <OrderTypeMultiSelect
          folder={folder}
          orderTypes={orderTypes}
          readOnly={readOnlyOrderTypes}
          onChange={onOrderTypesChange}
        />
      </td>
      <td>
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 30px;
            width: 100%;
          `}
          onClick={(e) => e.stopPropagation()}
        >
          <Popover
            content={
              <Menu>
                {!isDeactivated && (
                  <>
                    <MenuItem
                      icon="shield"
                      text="Manage User Access"
                      onClick={() => onOpenPermissions(folder)}
                    />
                    {isFybrAdmin && (
                      <MenuItem
                        icon="cube"
                        text="Manage Order Types"
                        onClick={() => onOpenOrderTypes(folder)}
                      />
                    )}
                    <MenuItem
                      icon="edit"
                      text="Edit Folder"
                      onClick={() => onEditFolder(folder.id)}
                    />
                    <MenuItem
                      icon="disable"
                      text="Deactivate"
                      onClick={() => onDeleteFolder(folder.id)}
                      intent="danger"
                    />
                  </>
                )}
                {isDeactivated && (
                  <MenuItem
                    icon={isActivating ? <Spinner size={16} /> : "tick"}
                    text={isActivating ? "Activating..." : "Activate"}
                    onClick={() => onActivateFolder(folder.id)}
                    intent="success"
                    disabled={isActivating}
                  />
                )}
              </Menu>
            }
            placement="bottom-end"
          >
            <Button size="small" icon="more" />
          </Popover>
        </div>
      </td>
    </tr>
  );
}

export default function AdminOrganizationFolders() {
  const { organization_id } = useParams<{ organization_id: string }>();
  const organizationId = Number(organization_id);

  const { currentUser } = useAuthState();
  const isCurrentUserAdmin = isOrgAdminUser(currentUser);
  const isFybrAdmin = isFybrAdminUser(currentUser);

  const [includeDeactivated, setIncludeDeactivated] = React.useState(false);
  const [permissionsDialogFolder, setPermissionsDialogFolder] =
    React.useState<Folder | null>(null);

  const {
    folders,
    users,
    groups,
    isLoadingFolders,
    isLoadingUsers,
    isLoadingGroups,
    usersError,
    groupsError,
  } = useFolderPermissions(
    organizationId,
    includeDeactivated,
    permissionsDialogFolder !== null
  );

  const { deleteFolder, activateFolder, isDeleting, isActivating } =
    useFolderMutations(organizationId);

  const orderTypesQuery = useQuery({
    queryKey: ["orderTypes", organizationId],
    queryFn: () => ApiAdmin.getOrderTypesForOrganization(organizationId),
  });

  const orderTypes = React.useMemo(() => {
    const all = orderTypesQuery.data?.order_types || [];
    return all.filter((ot) => !ot.deleted_at);
  }, [orderTypesQuery.data?.order_types]);

  const updateFolderOrderTypesMutation =
    useUpdateFolderOrderTypesMutation(organizationId);

  const [editDialogFolder, setEditDialogFolder] = React.useState<Folder | null>(
    null
  );
  const [deleteDialogFolder, setDeleteDialogFolder] = React.useState<{
    id: number;
    name: string;
  } | null>(null);
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [orderTypesDialogFolder, setOrderTypesDialogFolder] =
    React.useState<Folder | null>(null);
  const [filterText, setFilterText] = React.useState("");
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
    "asc"
  );

  const prevIsDeletingRef = React.useRef(isDeleting);

  React.useEffect(() => {
    if (
      prevIsDeletingRef.current &&
      !isDeleting &&
      deleteDialogFolder !== null
    ) {
      setDeleteDialogFolder(null);
    }
    prevIsDeletingRef.current = isDeleting;
  }, [isDeleting, deleteDialogFolder]);

  const handleEditFolder = (folderId: number) => {
    const folder = folders.find((f) => f.id === folderId);
    if (folder) {
      setEditDialogFolder(folder);
    }
  };

  const handleDeleteFolder = (folderId: number) => {
    const folder = folders.find((f) => f.id === folderId);
    if (folder) {
      setDeleteDialogFolder({ id: folder.id, name: folder.label });
    }
  };

  const handleActivateFolder = (folderId: number) => {
    activateFolder(folderId);
  };

  const handleOrderTypesChange = (
    folderId: number,
    organizationOrderTypeIds: number[]
  ) => {
    updateFolderOrderTypesMutation.mutate({
      folderId,
      organizationOrderTypeIds,
    });
  };

  const filteredFolders = React.useMemo(() => {
    if (!folders) return [];

    const filtered = folders.filter((folder) => {
      if (!filterText.trim()) return true;
      const searchText = filterText.toLowerCase();
      return folder.label.toLowerCase().includes(searchText);
    });

    return filtered.sort((a, b) => {
      const comparison = a.label.localeCompare(b.label);
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [folders, filterText, sortDirection]);

  const tableScrollRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useAdminTableVirtualizer(
    filteredFolders,
    tableScrollRef
  );

  let content;
  if (isLoadingFolders || orderTypesQuery.isLoading) {
    content = (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
        `}
      >
        <Spinner />
      </div>
    );
  } else if (folders.length === 0) {
    content = (
      <NonIdealState
        icon="folder-close"
        title="No Folders Found"
        description="Get started by creating your first folder."
        action={
          isCurrentUserAdmin ? (
            <Button
              icon="folder-new"
              text="Add Folder"
              intent="primary"
              onClick={() => setAddDialogOpen(true)}
            />
          ) : undefined
        }
      />
    );
  } else if (filteredFolders.length === 0 && filterText.trim()) {
    content = (
      <NonIdealState
        icon="search"
        title="No Folders Match Filter"
        description={`No folders found matching "${filterText}".`}
      />
    );
  } else {
    content = (
      <HTMLTable
        interactive
        css={css`
          width: 100%;
          thead {
            position: sticky;
            top: 0;
            background-color: var(--neutral-6);
            z-index: 1;

            th {
              border-bottom: 1px solid var(--neutral-5);
            }
          }

          tbody tr:first-child td {
            box-shadow: none !important;
          }
        `}
      >
        <colgroup>
          <col width="25%" />
          <col width="10%" />
          <col />
          <col width="64px" />
        </colgroup>
        <thead>
          <tr>
            <th
              onClick={() =>
                setSortDirection(sortDirection === "asc" ? "desc" : "asc")
              }
              css={css`
                cursor: pointer;
                user-select: none;

                &:hover {
                  background-color: var(--background-dark-translucent);
                }
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                `}
              >
                <span>Name</span>
                <Icon
                  icon={sortDirection === "asc" ? "chevron-up" : "chevron-down"}
                />
              </div>
            </th>
            <th>Status</th>
            <th>Order Types</th>
            <th></th>
          </tr>
        </thead>
        <VirtualizedTableBody
          virtualizer={rowVirtualizer}
          colSpan={FOLDER_TABLE_COLUMN_COUNT}
          renderRow={(virtualRow) => {
            const folder = filteredFolders[virtualRow.index];
            return (
              <FolderRow
                key={folder.id}
                folder={folder}
                index={virtualRow.index}
                orderTypes={orderTypes}
                isFybrAdmin={isFybrAdmin}
                isActivating={isActivating}
                onOpenPermissions={setPermissionsDialogFolder}
                onOpenOrderTypes={setOrderTypesDialogFolder}
                onEditFolder={handleEditFolder}
                onDeleteFolder={handleDeleteFolder}
                onActivateFolder={handleActivateFolder}
                onOrderTypesChange={handleOrderTypesChange}
                measureRef={rowVirtualizer.measureElement}
              />
            );
          }}
        />
      </HTMLTable>
    );
  }

  return (
    <div
      css={css`
        background-color: var(--neutral-6);
        height: 100%;
        display: flex;
        flex-direction: column;
      `}
    >
      {!isLoadingFolders && (
        <div
          css={css`
            padding: 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: var(--neutral-7);
            border-bottom: 1px solid var(--neutral-5);
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 1rem;
              flex: 1;
              padding-right: 1rem;
            `}
          >
            <InputGroup
              leftIcon="search"
              placeholder="Filter folders..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              css={css`
                width: 300px;
              `}
            />
            <Switch
              css={css`
                margin: 0;
              `}
              label="Show deactivated"
              checked={includeDeactivated}
              onChange={(e) => setIncludeDeactivated(e.currentTarget.checked)}
            />
          </div>
          <Button
            icon="folder-new"
            text="Add Folder"
            intent="primary"
            onClick={() => setAddDialogOpen(true)}
          />
        </div>
      )}
      <div
        ref={tableScrollRef}
        css={css`
          flex: 1;
          overflow: auto;
        `}
      >
        {content}
      </div>
      <AddFolderDialog
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        organizationId={organizationId}
      />
      <EditFolderDialog
        editDialogFolder={editDialogFolder}
        onClose={() => setEditDialogFolder(null)}
        organizationId={organizationId}
      />
      {permissionsDialogFolder && (
        <FolderPermissionsDialog
          key={permissionsDialogFolder?.id || "closed"}
          permissionsDialogFolder={permissionsDialogFolder}
          onClose={() => setPermissionsDialogFolder(null)}
          users={users}
          groups={groups}
          organizationId={organizationId}
          isLoadingUsersAndGroups={isLoadingUsers || isLoadingGroups}
          hasUsersAndGroupsError={Boolean(usersError || groupsError)}
        />
      )}
      {orderTypesDialogFolder && (
        <FolderOrderTypesDialog
          folder={orderTypesDialogFolder}
          organizationId={organizationId}
          isOpen
          onClose={() => setOrderTypesDialogFolder(null)}
        />
      )}
      <ConfirmationDialog
        entityName={deleteDialogFolder?.name || ""}
        entityType="Folder"
        message="This folder will be deactivated."
        isOpen={deleteDialogFolder !== null}
        onClose={() => setDeleteDialogFolder(null)}
        onConfirm={() =>
          deleteDialogFolder && deleteFolder(deleteDialogFolder.id)
        }
        isPending={isDeleting}
        actionText="Deactivate"
        actioningText="Deactivating..."
        titleAction="Deactivate"
        icon="disable"
      />
    </div>
  );
}
